import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Star } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const LOGO_URL = 'https://customer-assets-39nsmqrw.emergentagent.net/job_crafted-spaces-21/artifacts/9qjq48qm_Image.jpeg';
const PHONE = '(505) 589-3461';
const PHONE_HREF = 'tel:+15055893461';

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isHome = pathname === '/';
    // Anchor links only exist on the homepage; from other routes, send users home to the anchor.
    const anchor = (hash) => (hash.startsWith('/') ? hash : (isHome ? hash : `/${hash}`));

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const links = [
        { href: '#estimate', label: 'Estimate', id: 'nav-estimate' },
        { href: '#services', label: 'Services', id: 'nav-services' },
        { href: '#work', label: 'Work', id: 'nav-work' },
        { href: '#craft', label: 'Craft', id: 'nav-craft' },
        { href: '/paintvision', label: 'PaintVision', id: 'nav-paintvision' },
        { href: '#contact', label: 'Contact', id: 'nav-contact' },
    ];

    return (
        <>
            <motion.header
                data-testid="msd-nav"
                initial={{ y: -30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1, delay: 0.2, ease: [0.7, 0, 0.2, 1] }}
                className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
                    scrolled ? 'bg-ink-900/85 border-b border-white/5' : 'bg-transparent'
                }`}
            >
                <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-4 flex items-center justify-between gap-6">
                    {/* LOGO — top-left */}
                    <a
                        href={anchor('#top')}
                        data-testid="msd-logo"
                        className="flex items-center gap-3 group shrink-0"
                        aria-label="MSD Painting Services home"
                    >
                        <span className="inline-flex items-center justify-center bg-bone-100 rounded-sm p-1.5 shadow-[0_2px_20px_rgba(232,172,101,0.15)]">
                            <img
                                src={LOGO_URL}
                                alt="MSD Painting Services"
                                className="h-9 w-auto object-contain"
                                loading="eager"
                            />
                        </span>
                        <div className="hidden sm:block leading-none">
                            <div className="font-mono text-[9px] tracking-[0.32em] uppercase text-bone-500">
                                <span className="text-adobe-500">◆</span> Veteran Owned · New Mexico
                            </div>
                        </div>
                    </a>

                    {/* Desktop nav — top-right */}
                    <nav className="hidden md:flex items-center gap-10">
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={anchor(l.href)}
                                data-testid={l.id}
                                className="link-underline font-mono text-[11px] tracking-[0.28em] uppercase text-bone-200 hover:text-adobe-500 transition-colors"
                            >
                                {l.label}
                            </a>
                        ))}
                        <button
                            type="button"
                            onClick={() => navigate('/estimate')}
                            data-testid="nav-cta-phone"
                            className="btn-pill btn-pill--solid"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-adobe-500" />
                            Get an estimate
                        </button>
                    </nav>

                    {/* Mobile toggle */}
                    <button
                        data-testid="msd-mobile-toggle"
                        className="md:hidden p-2 text-bone-100"
                        onClick={() => setOpen((o) => !o)}
                        aria-label="Toggle navigation"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-40 bg-ink-950 md:hidden pt-24 px-8"
                    >
                        <div className="flex flex-col gap-8">
                            {links.map((l, i) => (
                                <motion.a
                                    key={l.href}
                                    href={anchor(l.href)}
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.08 * i }}
                                    onClick={() => setOpen(false)}
                                    className="font-display text-4xl text-bone-100"
                                    data-testid={`mobile-${l.id}`}
                                >
                                    {l.label}
                                </motion.a>
                            ))}
                            <button
                                type="button"
                                onClick={() => { setOpen(false); navigate('/estimate'); }}
                                className="btn-pill btn-pill--solid mt-8 self-start"
                                data-testid="mobile-cta-phone"
                            >
                                Get a free estimate
                            </button>
                            <div className="mt-4 font-mono text-[10px] tracking-[0.28em] uppercase text-bone-500 flex items-center gap-2">
                                <Star size={10} className="text-adobe-500" strokeWidth={1.5} />
                                Veteran Owned & Operated · Serving all of New Mexico
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
